import Image from 'next/image'
import Layout, { LargeLayout } from 'components/layout'
import { Color, descriptionMultiCols } from '../sharedTypes'
import { CFImage } from '@/data/portfolio/type/client-index'
import Instagram from '@/components/social/instagram'
import classNames from 'classnames'

type Urls = {
  instagram: string
  website: string
}

export type PresentationSlideProps = {
  title: string
  urls: Urls
  images: CFImage[]
  subtitle: string
  types: string[]
  description?: descriptionMultiCols
  descriptionRight?: boolean
  variant?: number
  titleColor?: Color
}

export default function PresentationSlide({
  title,
  types,
  images,
  subtitle,
  description,
  descriptionRight = false,
  urls = { instagram: '', website: '' },
  variant = 0,
  titleColor = Color.orange
}: PresentationSlideProps) {
  return (
    <LargeLayout>
      <div className="relative space-y-12 pt-12 md:grid md:h-screen md:max-h-[62rem] md:grid-cols-3 md:gap-12 md:space-y-0 md:py-24">
        <div className="pt-12 md:text-right">
          <h1
            className={classNames('font-serif text-6xl font-bold md:text-7xl', {
              'text-orange': titleColor === Color.orange,
              'text-blue': titleColor === Color.blue,
              'text-yellow': titleColor === Color.yellow
            })}>
            {title}
          </h1>
          <ul className="mt-2 space-y-2">
            {types.map((thisType) => (
              <li key={thisType} className="uppercase tracking-wider">
                {thisType}
              </li>
            ))}
          </ul>
          <div className="mt-4 inline-flex space-x-3">
            {urls.website ? (
              <a href={urls.website} target="_blank" rel="noopener noreferrer">
                <svg className="h-6" viewBox="0 0 44 36" xmlns="http://www.w3.org/2000/svg">
                  <path
                    className="fill-current"
                    d="M5 30C4.2 30 3.5 29.7 2.9 29.1C2.3 28.5 2 27.8 2 27V3C2 2.2 2.3 1.5 2.9 0.9C3.5 0.3 4.2 0 5 0H39C39.8 0 40.5 0.3 41.1 0.9C41.7 1.5 42 2.2 42 3V27C42 27.8 41.7 28.5 41.1 29.1C40.5 29.7 39.8 30 39 30H5ZM5 27H39V3H5V27ZM1.5 36C1.06667 36 0.708333 35.8583 0.425 35.575C0.141667 35.2917 0 34.9333 0 34.5C0 34.0667 0.141667 33.7083 0.425 33.425C0.708333 33.1417 1.06667 33 1.5 33H42.5C42.9333 33 43.2917 33.1417 43.575 33.425C43.8583 33.7083 44 34.0667 44 34.5C44 34.9333 43.8583 35.2917 43.575 35.575C43.2917 35.8583 42.9333 36 42.5 36H1.5ZM5 27V3V27Z"
                    fill="black"
                  />
                </svg>
              </a>
            ) : null}
            {urls.instagram ? (
              <a href={urls.instagram} target="_blank" rel="noopener noreferrer">
                <Instagram className="h-6" />
              </a>
            ) : null}
          </div>
        </div>

        <div
          className={classNames('md:row-span-0 w-full md:col-span-2 md:h-full md:pr-24 md:pb-0', {
            'md:grid md:grid-cols-2 md:gap-12': descriptionRight,
            'md:flex md:flex-col md:justify-center md:!pr-0': !descriptionRight && !description
          })}>
          <div
            className={classNames('grid gap-6', {
              img_grid_container_rows: descriptionRight,
              img_grid_container_cols: !descriptionRight
            })}>
            {images.map((thisImage) => (
              <div key={thisImage.url}>
                <Image
                  src={thisImage.url}
                  width={thisImage.width}
                  height={thisImage.height}
                  className="h-full w-auto"
                />
              </div>
            ))}
          </div>
          {/* <div
            className="absolute -bottom-72 -left-24 -z-10 hidden h-[600px] w-[300px] rounded-r-full border-4 border-blue-200 bg-transparent md:block"
            aria-hidden="true"
          /> */}
          {description ? (
            <div className="mt-12">
              <h2 className="font-serif text-5xl font-bold">{subtitle}</h2>
              <div
                className={classNames('mt-4 space-y-6 md:flex md:space-y-0 md:space-x-6', {
                  'flex-col !space-y-6 !space-x-0': descriptionRight
                })}>
                {description.content.map((thisContent) => (
                  <p key={thisContent} className="hyphenate flex-1 font-light leading-relaxed">
                    {thisContent}
                  </p>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {variant === 1 ? (
          <div
            aria-hidden="true"
            className="absolute top-48 -right-12 -z-10 hidden h-[30rem] w-[15rem] rounded-l-full bg-orange-200 md:block"
          />
        ) : null}

        {variant === 2 ? (
          <div
            className="absolute bottom-24 left-12 -z-10 hidden h-[15rem] w-[30rem] rounded-t-full border-4 border-blue-200 bg-transparent md:block"
            aria-hidden="true"
          />
        ) : null}
      </div>
      <style jsx>{`
        .img_grid_container_rows {
          grid-template-rows: repeat(${images.length}, minmax(0, 1fr));
        }

        .img_grid_container_cols {
          grid-template-columns: repeat(${images.length}, minmax(0, 1fr));
        }
      `}</style>
    </LargeLayout>
  )
}
